import { atom } from "jotai";
import { ChannelType, PaginatedResponseType } from "../../globals/types";
import { IInvoice } from "../invoices";
import { QueueStatus } from "../scanner";

export const ordersAtom = atom<PaginatedResponseType<IOrder>>({ results: [], count: 0, hasNext: false, hasBefore: false, channel: ChannelType.WALLMATES });
export const ordersLoadingAtom = atom<boolean>(false);
export const ordersCountAtom = atom<number>(0);
export const ordersLastQueryAtom = atom<IGetOrdersQuery | null>(null);
export const orderLoadingAtom = atom<boolean>(false);

// Infinite scroll atoms
export const ordersAccumulatedAtom = atom<IOrder[]>([]);
export const ordersHasMoreAtom = atom<boolean>(true);
export const ordersCurrentPageAtom = atom<number>(0);
export const ordersLoadingMoreAtom = atom<boolean>(false);
export const failedOrderQueueItemsAtom = atom<IFailedOrderQueue[]>([]);
export const failedOrderQueueItemsLoadingAtom = atom<boolean>(false);

// Scroll position & highlight atoms
export const orderListScrollPositionAtom = atom<number>(0);
export const lastViewedOrderIdAtom = atom<number | null>(null);
export const navigatedFromOrderDetailAtom = atom<boolean>(false);
export const singleOrderAtom = atom<IOrderDTO | null>(null);
export const singleOrderLoadingAtom = atom<boolean>(false);

/** Order ids the user fulfilled during a Prev/Next run; pruned from the
 *  accumulated list when the user returns to the orders list. */
export const pendingRemovalOrderIdsAtom = atom<number[]>([]);

/** True while the user is inside a list -> detail -> Prev/Next session.
 *  Gates whether Prev/Next renders at all and whether filters survive a
 *  return trip to /orders. Set on row click, cleared on a fresh list visit. */
export const orderNavSessionActiveAtom = atom<boolean>(false);

// Orders list UI state. These live in the service layer rather than in
// `Orders.component.tsx` so the lazily-loaded order DETAIL chunk can read the
// active filters without pulling the entire orders-list chunk into its bundle.
export const ordersTabsAtom = atom<number>(0);
export const ordersFilterAtom = atom<GetOrdersQueryFilters>({});
export const ordersViewModeAtom = atom<'orders' | 'failed'>('orders');

export interface IOrder {
    id: number;
    order_number: number;
    order_total: number;
    order_date: string;
    brand_id: number;
    order_status: string;
    fulfillment_status: string;
    customer_first: string;
    customer_last: string;
    jdml: string;
    scanned: boolean;
    /** Order was created via a sandbox API key (never billed, never printed). */
    is_sandbox?: boolean;
}

export interface IFailedOrderQueue {
    order_queue_item: IOrderQueue;
    line_items: IFailedOrderQueueLineItem[];
}

export interface IOrderQueue {
    id?: number;
    created_at?: string;
    order_number: number;
    process_status: QueueStatus;
    brand_id: number;
    status_message: string;
    environment: string;
}

export interface IFailedOrderQueueLineItem {
    design_name: string;
    has_design: boolean;
    has_design_metadata: boolean;
    image_url: string;
}

export enum IFulfillmentStatus {
    SUCCESS = 'SUCCESS',
    CANCELLED = 'CANCELLED',
    FULFILLED = 'FULFILLED',
    PARTIAL = 'PARTIAL',
    UNFULFILLED = 'UNFULFILLED'
}

export enum IOrderStatus {
    NEW = 'NEW',
    PAST_DUE = 'PAST DUE',
    ISSUE = 'ISSUE',
    FULFILLED = 'FULFILLED',
    UNFULFILLED = 'UNFULFILLED',
    PARTIALLY_FULFILLED = 'PARTIALLY_FULFILLED'
}

export interface GetOrdersQueryFilters {
    brand_id?: number;
    exclude_brand_ids?: number[];
    /** One or more fulfillment status names (e.g. "UNFULFILLED", "PARTIAL").
     *  Backend RPC accepts either a single string (legacy) or an array.
     *  New code should pass arrays; the UI multi-select always does. */
    fulfillment_status?: string | string[];
    query_string?: string;
    has_notes?: boolean;
    missing_images?: boolean;
    /** true = only orders with at least one non-overstock line item not yet drop validated. */
    not_drop_validated?: boolean;
    is_scanned?: boolean;
    /** true = only sandbox (test API) orders, false = only live orders, omitted = all. */
    is_sandbox?: boolean;
    from_date?: string;
    to_date?: string;
    material_ids?: string[];
}

export interface IGetOrdersQuery {
    page_count: number;
    page: number;
    filter_by: GetOrdersQueryFilters;
}

/** Classifies how an order originated. Mirrors order-handler's `OrderSource`. */
export type OrderSource = 'SHOPIFY' | 'INTERIOR_DESIGNER' | 'API' | 'INTERNAL';

/**
 * True for orders created through the API intake path — third-party API orders and
 * the brand's own internal self-orders.
 *
 * Their line-item `sku` is not a SKU: that path stores a pipe-encoded blob
 * (`sku|ref|rpeHeight|thumbnailUrl|tileWidth`) whose thumbnail segment is a full CDN
 * URL, and for internal orders the leading real-SKU segment is empty. There is
 * nothing meaningful to show, so callers hide the field entirely rather than print
 * the blob.
 */
export const isApiBasedOrder = (order?: { order_source?: string | null } | null): boolean => {
    const source = (order?.order_source || '').toUpperCase();
    return source === 'API' || source === 'INTERNAL';
};

export interface IOrderDTO {
    id: number;
    order_number: number;
    shopify_order_number?: number;
    status: {
        name: string;
    }
    fulfillment_status: {
        id: number;
        name: string;
    }
    order_total: number;
    customer: {
        id: number;
        customer_id: number;
        first_name: string;
        last_name: string;
        shipping_first_name: string;
        shipping_last_name: string;
        addresses: {
            id: number;
            address_type: number;
            street_address: string;
            city: string;
            state: string;
            zip: string;
            country: string;
            /**
             * Set when this row is the per-order address snapshot captured at
             * checkout. `null` means it is a customer-profile address, which may
             * be stale for any given order. Always prefer the row whose
             * `order_id` matches the order being displayed.
             */
            order_id: number | null;
        }[]
    }
    order_notes: string;
    payment_method: string;
    /** Order was created via a sandbox API key (never billed, never printed). */
    is_sandbox?: boolean;
    /** How the order originated. Mirrors the DB CHECK on order.orders.order_source. */
    order_source?: OrderSource;
    discount: number;
    brand: {
        id: number;
        name: string;
        shopify_brand_name: string;
        use_expensive_box: boolean;
        drop_pause_time?: number;
    }
    status_update: string;
    order_date: string;
    metadata: {
        jdml: string;
        name?: string;
    }[];
    invoices: IInvoice[];
}

export interface IDraftOrderResponse {
    invoice_url?: string;
    draft_order_id?: string;
    checkout_url?: string;
}

export type BulkFulfillSkipReason = 'already_fulfilled' | 'cancelled' | 'status_changed_concurrently';

export interface IBulkFulfillResult {
    updated: number[];
    skipped: { id: number; reason: BulkFulfillSkipReason }[];
    failed: { id: number; message: string }[];
}
